import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedule } from '../context/ScheduleContext';
import { parseISO, differenceInMinutes } from 'date-fns';

const hasNotificationAPI = typeof Notification !== 'undefined';
const PMTILES_PATH = '/edc-mexico.pmtiles';

function requestLocationPermission(): void {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
  navigator.geolocation.getCurrentPosition(
    () => {},
    () => {},
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
  );
}

export const NotificationManager = () => {
  const { t } = useTranslation();
  const { allPerformances, favorites, settings, virtualTime } = useSchedule();
  const lastCheckRef = useRef<Date>(virtualTime);

  useEffect(() => {
    // Ask permissions on startup so any first-open page can become offline-ready faster.
    if (hasNotificationAPI && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (typeof navigator !== 'undefined' && 'permissions' in navigator && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'prompt' || status.state === 'granted') {
            requestLocationPermission();
          }
        })
        .catch(() => {
          requestLocationPermission();
        });
    } else {
      requestLocationPermission();
    }

    if (typeof window !== 'undefined' && navigator.onLine) {
      const warmMapData = () => {
        fetch(PMTILES_PATH).catch(() => {});
      };
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(
          warmMapData
        );
      } else {
        setTimeout(warmMapData, 1500);
      }
    }
  }, []);

  const notifiedPerformances = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Check every 30 seconds to be safe
    const interval = setInterval(() => {
      const now = virtualTime;
      const notifyMinutes = settings.notificationBefore;

      allPerformances.forEach(perf => {
        if (!favorites.includes(perf.dj)) return;

        const startTime = parseISO(perf.start_time);
        const diff = differenceInMinutes(startTime, now);
        const perfId = `${perf.dj}-${perf.start_time}`;

        // If within notification window (e.g. 15 mins or less, but not started yet)
        if (diff <= notifyMinutes && diff > 0) {
           if (!notifiedPerformances.current.has(perfId)) {
             if (hasNotificationAPI && Notification.permission === 'granted') {
                new Notification(t('notifications.title', { dj: perf.dj }), {
                    body: t('notifications.body', { dj: perf.dj, minutes: diff, stage: perf.stage }),
                    icon: '/pwa-192x192.png'
                });
             }
             notifiedPerformances.current.add(perfId);
           }
        } else {
            // Reset if time passed or too far (e.g. if user resets time backward)
            if (diff > notifyMinutes + 5 || diff < -60) {
                notifiedPerformances.current.delete(perfId);
            }
        }
      });
      
      lastCheckRef.current = now;
    }, 30000); 

    return () => clearInterval(interval);
  }, [allPerformances, favorites, settings, virtualTime, t]);

  return null;
};
